import { memo } from 'react';
import { Chip, ColorsV2, useThemeColors } from '@electricjs/arc';
import { formatSlugToDisplayName } from 'common/utils';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  TOOL_NAMES,
  CART_TOOL_DISPLAY_REASONS,
  CONFLICT_RESOLUTION,
  CartToolDisplayReason,
} from '@/helpers/aiChat/tools';
import { ToolDisplayStatus } from '@/pages/AIChatCopilotKit/types';

type AIToolChipProps = {
  id: string;
  toolSlug: string;
  toolDisplayReason?: CartToolDisplayReason;
  toolArgs?: string;
  variant: ToolDisplayStatus;
};

/**
 * Backend tool slugs for display name mapping
 */
const ToolSlug = {
  GET_ORGANIZATION_EMPLOYEES: 'get_organization_employees',
  GET_EMPLOYEE_ASSETS: 'get_employee_assets',
  GET_MY_EMPLOYEE_DETAILS: 'get_my_employee_details',
  GET_MY_DEVICES: 'get_my_devices',
  LIST_ORGANIZATION_ASSETS: 'list_organization_assets',
  GET_ORGANIZATION_APPLICATIONS: 'get_organization_applications',
  CAN_AUTOMATICALLY_CREATE_ACCOUNT_FOR_ORGANIZATION_APPLICATION:
    'can_automatically_create_account_for_organization_application',
  LIST_EMPLOYEE_APPLICATION_ACCOUNTS: 'list_employee_application_accounts',
  REQUEST_ACCOUNT_CREATION_FOR_APPLICATION:
    'request_account_creation_for_application',
  GET_ORGANIZATION_DEVICE_STATUS: 'get_organization_device_status',
  GET_ORGANIZATION_DEVICE_ISSUES: 'get_organization_device_issues',
  GET_SYSTEM_WITH_POLICY_STATS_BY_SERIAL_NUMBER:
    'get_system_with_policy_stats_by_serial_number',
  GET_SYSTEM_RECOVERY_KEY: 'get_system_recovery_key',
  LOCK_DEVICE: 'lock_device',
} as const;

type ToolSlug = (typeof ToolSlug)[keyof typeof ToolSlug];

export const AIToolChip = memo(
  ({ id, toolSlug, toolDisplayReason, toolArgs, variant }: AIToolChipProps) => {
    const { t } = useTranslation('ai-chat');
    const [
      completedColorBg,
      completedColorText,
      approvalRequiredColorBg,
      approvalRequiredColorText,
      rejectedColorBg,
      rejectedColorText,
      pendingColorBg,
      pendingColorText,
    ] = useThemeColors([
      ColorsV2.SUCCESS_LIGHTEST,
      ColorsV2.SUCCESS_DARKEST,
      ColorsV2.WARNING_LIGHTEST,
      ColorsV2.WARNING_DARKEST,
      ColorsV2.ERROR_LIGHTEST,
      ColorsV2.ERROR_DARK,
      ColorsV2.INFO_LIGHTEST,
      ColorsV2.INFO_DARK,
    ]);

    const toolDisplayNames: Record<ToolSlug, string> = {
      [ToolSlug.GET_ORGANIZATION_EMPLOYEES]: t(
        'tools.getOrganizationEmployees'
      ),
      [ToolSlug.GET_EMPLOYEE_ASSETS]: t('tools.getEmployeeAssets'),
      [ToolSlug.GET_MY_EMPLOYEE_DETAILS]: t('tools.getMyEmployeeDetails'),
      [ToolSlug.GET_MY_DEVICES]: t('tools.getMyDevices'),
      [ToolSlug.LIST_ORGANIZATION_ASSETS]: t('tools.listOrganizationAssets'),
      [ToolSlug.GET_ORGANIZATION_APPLICATIONS]: t(
        'tools.getOrganizationApplications'
      ),
      [ToolSlug.CAN_AUTOMATICALLY_CREATE_ACCOUNT_FOR_ORGANIZATION_APPLICATION]:
        t('tools.canAutoCreateAccount'),
      [ToolSlug.LIST_EMPLOYEE_APPLICATION_ACCOUNTS]: t(
        'tools.listEmployeeApplicationAccounts'
      ),
      [ToolSlug.REQUEST_ACCOUNT_CREATION_FOR_APPLICATION]: t(
        'tools.requestAccountCreation'
      ),
      [ToolSlug.GET_ORGANIZATION_DEVICE_STATUS]: t(
        'tools.getOrganizationDeviceStatus'
      ),
      [ToolSlug.GET_ORGANIZATION_DEVICE_ISSUES]: t(
        'tools.getOrganizationDeviceIssues'
      ),
      [ToolSlug.GET_SYSTEM_WITH_POLICY_STATS_BY_SERIAL_NUMBER]: t(
        'tools.getSystemWithPolicyStats'
      ),
      [ToolSlug.GET_SYSTEM_RECOVERY_KEY]: t('tools.getSystemRecoveryKey'),
      [ToolSlug.LOCK_DEVICE]: t('tools.lockDevice'),
    };

    // For add_products_to_cart, determine display name based on available data
    let toolName;
    if (toolSlug === TOOL_NAMES.ADD_PRODUCTS_TO_CART) {
      // Primary: Use structured displayReason from tool result (post-execution)
      if (toolDisplayReason) {
        switch (toolDisplayReason) {
          case CART_TOOL_DISPLAY_REASONS.KEEP_CART:
            toolName = 'Switch employee (keep cart)';
            break;
          case CART_TOOL_DISPLAY_REASONS.CLEAR_CART:
            toolName = 'Clear cart';
            break;
          case CART_TOOL_DISPLAY_REASONS.CONFLICT_CHECK:
            toolName = 'Checked cart for conflicts';
            break;
          case CART_TOOL_DISPLAY_REASONS.SWITCH_EMPLOYEE:
            toolName = 'Switch employee';
            break;
          case CART_TOOL_DISPLAY_REASONS.ADD_PRODUCTS:
            toolName = 'Add products to cart';
            break;
          case CART_TOOL_DISPLAY_REASONS.NAVIGATE_TO_CART:
            toolName = 'Review cart contents';
            break;
          case CART_TOOL_DISPLAY_REASONS.ADD_TO_EXISTING:
            toolName = 'Add to existing cart';
            break;
        }
      }
      // Fallback: Parse toolArgs for pre-execution states (NEEDS_APPROVAL, PENDING, RUNNING)
      else if (
        toolArgs &&
        (variant === ToolDisplayStatus.NEEDS_APPROVAL ||
          variant === ToolDisplayStatus.PENDING ||
          variant === ToolDisplayStatus.RUNNING)
      ) {
        try {
          const args = JSON.parse(toolArgs);
          const hasProductIds = args.product_ids && args.product_ids.length > 0;
          const conflictResolution = args.conflict_resolution;

          // Handle conflict resolution cases first
          if (conflictResolution === CONFLICT_RESOLUTION.NAVIGATE_TO_CART) {
            toolName = 'Review cart contents';
          } else if (
            conflictResolution === CONFLICT_RESOLUTION.ADD_TO_EXISTING
          ) {
            toolName = 'Add to existing cart';
          } else if (conflictResolution === CONFLICT_RESOLUTION.KEEP_CART) {
            toolName = 'Switch employee (keep cart)';
          } else if (conflictResolution === CONFLICT_RESOLUTION.CLEAR_CART) {
            toolName = 'Clear cart';
          }
          // No conflict resolution provided
          else if (!conflictResolution) {
            if (!hasProductIds) {
              toolName = 'Switch employee';
            } else {
              // Has products but no conflict resolution
              // This will check for cart conflicts first, then add
              const productCount = args.product_ids?.length || 0;
              toolName = `Check cart and add ${productCount === 1 ? 'product' : 'products'}`;
            }
          } else {
            toolName = 'Add products to cart';
          }
        } catch (e) {
          console.warn(
            `[AIToolChip] Failed to parse toolArgs for ${id}:`,
            toolArgs,
            e
          );
          toolName =
            variant === ToolDisplayStatus.PENDING ||
            variant === ToolDisplayStatus.RUNNING
              ? 'Processing...'
              : 'Add products to cart';
        }
      }
      // Final fallback: Default to generic text
      else {
        // If we're in a completed state without displayReason, something went wrong
        if (variant === ToolDisplayStatus.COMPLETED) {
          console.warn(
            `[AIToolChip] Missing displayReason for completed cart tool ${id}`
          );
        }
        toolName =
          variant === ToolDisplayStatus.PENDING ||
          variant === ToolDisplayStatus.RUNNING
            ? 'Processing...'
            : 'Add products to cart';
      }
    } else {
      // For other tools, use predefined display names or format the slug
      toolName = toolDisplayNames[toolSlug as ToolSlug]
        ? capitalize(toolDisplayNames[toolSlug as ToolSlug])
        : formatSlugToDisplayName(toolSlug);
    }

    switch (variant) {
      case ToolDisplayStatus.COMPLETED:
        return (
          <Chip
            id={id}
            backgroundColor={completedColorBg}
            variant="small"
            icon="check"
            textColor={completedColorText}>
            {toolName}
          </Chip>
        );
      case ToolDisplayStatus.NEEDS_APPROVAL:
        return (
          <Chip
            id={id}
            backgroundColor={approvalRequiredColorBg}
            variant="small"
            icon="question-message"
            textColor={approvalRequiredColorText}>
            {toolName}
          </Chip>
        );
      case ToolDisplayStatus.REJECTED:
        return (
          <Chip
            id={id}
            backgroundColor={rejectedColorBg}
            variant="small"
            icon="close"
            textColor={rejectedColorText}>
            {toolName}
          </Chip>
        );
      case ToolDisplayStatus.PENDING:
      case ToolDisplayStatus.RUNNING:
        return (
          <Chip
            id={id}
            backgroundColor={pendingColorBg}
            variant="small"
            icon="sparkles"
            textColor={pendingColorText}>
            {toolName}
          </Chip>
        );
    }
  }
);

AIToolChip.displayName = 'AIToolChip';
